import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import type { ItemId } from '@goodtime/shared';
import { Easings, TweenGroup } from '../../core/Tween';

/**
 * Per-avatar item effect — bomb drops on a target, lightning
 * strikes, coin pile lands on a deployer. Producer brief
 * 2026-05-14 + 2026-05-15: every action-card flash gets a
 * companion render on each affected seat's avatar so the player
 * connects the central narrative beat to its in-game victim /
 * beneficiary. Coin-delta floater ("−5" red / "+5" green)
 * follows below the avatar with a fade-up.
 *
 * <p>The host scene mounts one instance per (seatId, card) pair
 * and calls {@link play} to kick the animation; the widget
 * self-cleans by hiding once both tweens complete.
 */

export interface WheelResolveSeatFxOptions {
  /** Avatar diameter in canvas pixels (drives icon + floater scale). */
  avatarDiameter: number;
  /** Optional coin texture for the gain/loss floater. When set, a
   *  coin glyph sits to the LEFT of the "+N" / "-N" digits so the
   *  player can read the magnitude as currency at a glance.
   *  Producer feedback 2026-05-17 — "show the coin icon in the
   *  win/loss so it's obvious". */
  coinTexture?: Texture | null;
}

export interface SeatFxPlayConfig {
  itemId: ItemId;
  iconTexture: Texture | null;
  /** Signed coin delta to float — positive renders green, negative
   *  red, 0 hides the floater (e.g. shield-blocked HIT). */
  seatDelta: number;
  /** Whether this seat is the source / target — drives the
   *  "collect" animation choice for transfer items (LIGHTNING /
   *  SHAKEDOWN) when isSource is true with a positive delta. */
  isSource: boolean;
  isTarget: boolean;
  /** Whether the source-side beat should use the SHIELD-blocked
   *  visual (only relevant when isTarget + blocked). */
  blocked: boolean;
  /** Words to float in place of the coin delta, e.g. "NOTHING TO
   *  STEAL" on the rival a Shakedown found empty-handed; the icon
   *  greys out as they rise. */
  note?: string;
}

export class WheelResolveSeatFx extends Container {
  private readonly iconHost: Container;
  private iconSprite: Sprite | null = null;
  private readonly shieldBubble: Graphics;
  /** Group that holds the coin glyph + delta digits + direction
   *  arrow, so we can fade / lift / anchor them as one. Anchored on
   *  its midpoint via per-frame layout in {@link play}. */
  private readonly deltaHost: Container;
  private readonly coinSprite: Sprite | null;
  private readonly coinDelta: Text;
  /** Directional arrow drawn to the right of the digits — ▲ green
   *  for gains, ▼ red for losses. Producer feedback 2026-05-17 —
   *  "up arrow (green) for coins gained or down arrow (red) for
   *  lost coins." */
  private readonly arrowGfx: Graphics;
  private readonly noteText: Text;
  private readonly tweens = new TweenGroup();
  private readonly opts: WheelResolveSeatFxOptions;

  constructor(options: WheelResolveSeatFxOptions) {
    super();
    this.opts = options;
    this.visible = false;

    this.iconHost = new Container();
    this.addChild(this.iconHost);

    this.shieldBubble = new Graphics();
    this.shieldBubble
      .circle(0, 0, options.avatarDiameter / 2 + 4)
      .stroke({ color: 0x5dd5ff, alpha: 1, width: 3 })
      .circle(0, 0, options.avatarDiameter / 2 + 4)
      .fill({ color: 0x5dd5ff, alpha: 0.18 });
    this.shieldBubble.visible = false;
    this.addChild(this.shieldBubble);

    this.deltaHost = new Container();
    this.deltaHost.visible = false;
    this.addChild(this.deltaHost);

    // Coin glyph — sits LEFT of the digits so "+12" reads as "🪙 +12".
    if (options.coinTexture) {
      const coin = new Sprite(options.coinTexture);
      coin.anchor.set(1, 0.5);
      const coinSize = Math.round(options.avatarDiameter * 0.34);
      coin.width = coinSize;
      coin.height = coinSize;
      this.deltaHost.addChild(coin);
      this.coinSprite = coin;
    } else {
      this.coinSprite = null;
    }

    this.coinDelta = new Text({
      text: '',
      style: {
        fontFamily: '"Fredoka", system-ui, -apple-system, sans-serif',
        fontSize: Math.round(options.avatarDiameter * 0.32),
        fontWeight: '900',
        fill: 0xff8e9d,
        stroke: { color: 0x1a0e2a, width: 3 },
      },
    });
    this.coinDelta.anchor.set(0, 0.5);
    this.deltaHost.addChild(this.coinDelta);

    this.arrowGfx = new Graphics();
    this.deltaHost.addChild(this.arrowGfx);

    this.noteText = new Text({
      text: '',
      style: {
        fontFamily: '"Fredoka", system-ui, -apple-system, sans-serif',
        fontSize: Math.round(options.avatarDiameter * 0.22),
        fontWeight: '900',
        fill: 0xf2eefc,
        stroke: { color: 0x1a0e2a, width: 3 },
        letterSpacing: 1,
        align: 'center',
        // Two short lines, so a seat at the screen's edge keeps it all
        // on screen.
        wordWrap: true,
        wordWrapWidth: options.avatarDiameter * 1.4,
        lineHeight: Math.round(options.avatarDiameter * 0.25),
      },
    });
    this.noteText.anchor.set(0.5, 0);
    this.noteText.visible = false;
    this.addChild(this.noteText);
  }

  play(cfg: SeatFxPlayConfig): void {
    this.visible = true;
    this.tweens.cancelAll();
    // Refresh icon sprite.
    if (this.iconSprite) {
      this.iconSprite.destroy();
      this.iconSprite = null;
    }
    if (cfg.iconTexture) {
      const sprite = new Sprite(cfg.iconTexture);
      sprite.anchor.set(0.5);
      const size = this.opts.avatarDiameter * 0.85;
      sprite.width = size;
      sprite.height = size;
      this.iconHost.addChild(sprite);
      this.iconSprite = sprite;
    }
    this.iconHost.alpha = 0;
    this.iconHost.y = 0;
    this.iconHost.scale.set(0.7);

    // Shield bubble overlay for blocked sabotage.
    this.shieldBubble.visible = cfg.blocked && cfg.isTarget;

    // Coin-delta floater — positive green, negative red, with a
    // coin glyph + directional arrow flanking the digits so the
    // gain/loss reads unmistakably.
    if (cfg.seatDelta !== 0) {
      this.deltaHost.visible = true;
      const isGain = cfg.seatDelta > 0;
      this.coinDelta.text = isGain ? `+${cfg.seatDelta}` : `${cfg.seatDelta}`;
      const colour = isGain ? 0x9de54e : 0xff8e9d;
      this.coinDelta.style.fill = colour;

      // Redraw the arrow — ▲ for gains, ▼ for losses. Both are
      // centred on the arrow Graphics' local origin so we can place
      // them in the row layout below.
      const arrowH = Math.round(this.opts.avatarDiameter * 0.22);
      const arrowW = Math.round(arrowH * 0.95);
      this.arrowGfx.clear();
      if (isGain) {
        this.arrowGfx
          .poly([0, -arrowH / 2, arrowW / 2, arrowH / 2, -arrowW / 2, arrowH / 2])
          .fill({ color: colour, alpha: 1 })
          .stroke({ color: 0x1a0e2a, alpha: 1, width: 1.5 });
      } else {
        this.arrowGfx
          .poly([-arrowW / 2, -arrowH / 2, arrowW / 2, -arrowH / 2, 0, arrowH / 2])
          .fill({ color: colour, alpha: 1 })
          .stroke({ color: 0x1a0e2a, alpha: 1, width: 1.5 });
      }

      // Lay out coin glyph (left) → digits (centre) → arrow (right)
      // as a horizontal row centred on deltaHost's origin (x=0).
      // Coin anchor is (1, 0.5) so its coinSprite.x sets its RIGHT
      // edge. Text anchor is (0, 0.5) so its coinDelta.x sets its
      // LEFT edge. Arrow is centred on arrowGfx.x.
      const gap = 4;
      const textW = Math.ceil(this.coinDelta.width);
      const coinW = this.coinSprite ? Math.ceil(this.coinSprite.width) : 0;
      const coinGap = coinW > 0 ? gap : 0;
      const arrowGap = gap;
      const totalW = coinW + coinGap + textW + arrowGap + arrowW;
      let cursorX = -totalW / 2;
      if (this.coinSprite) {
        cursorX += coinW;
        this.coinSprite.x = cursorX;
        this.coinSprite.y = 0;
        cursorX += coinGap;
      }
      this.coinDelta.x = cursorX;
      this.coinDelta.y = 0;
      cursorX += textW + arrowGap;
      this.arrowGfx.x = cursorX + arrowW / 2;
      this.arrowGfx.y = 0;
      this.deltaHost.y = this.opts.avatarDiameter * 0.5;
      this.deltaHost.alpha = 0;
    } else {
      this.deltaHost.visible = false;
    }

    // Animation profile per item — three families:
    //   • Drop-in (BOMB, COIN_STASH, LIGHTNING target, MAGNET) —
    //     icon slides down from above
    //   • Pulse (BOOST, X2_BONUS, SHIELD, BONUS_WEDGE source) —
    //     scale-up + brightness pulse
    //   • Slam (HIT, POW, SHAKEDOWN) — icon enters with a
    //     horizontal slam from the source direction
    const profile = animationProfile(cfg);
    this.runProfile(profile);
    if (this.iconSprite) this.iconSprite.tint = 0xffffff;
    this.noteText.visible = false;
    if (cfg.note) this.playNote(cfg.note);

    // Coin floater (coin glyph + delta digits + arrow) rises slowly
    // with a fade. Producer feedback 2026-05-26 — the floater needs
    // to outlive the central action-card dialogue so the player can
    // read both at once. Hold full opacity for the bulk of the beat,
    // fade only in the final 180ms so it disappears in sync with the
    // dialogue's hide() fade.
    if (this.deltaHost.visible) {
      const startY = this.deltaHost.y;
      this.tweens.start({
        durationMs: 1800,
        easing: Easings.easeOutCubic,
        onUpdate: (p) => {
          this.deltaHost.y = startY - 40 * p;
          // 0–10% fade in, 10–90% hold full, 90–100% fade out.
          if (p < 0.1) this.deltaHost.alpha = p / 0.1;
          else if (p > 0.9) this.deltaHost.alpha = (1 - p) / 0.1;
          else this.deltaHost.alpha = 1;
        },
      });
    }
  }

  /** The note rises under the avatar once the icon has landed, and
   *  the icon greys out with it: the item arrived and came up empty. */
  private playNote(note: string): void {
    const text = this.noteText;
    text.text = note;
    const startY = this.opts.avatarDiameter * 0.5;
    text.y = startY;
    text.alpha = 0;
    text.visible = true;
    const icon = this.iconSprite;
    this.tweens.start({
      durationMs: 1800,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        // Waits for the icon's 700 ms entry, then holds and fades
        // with it.
        const q = Math.max(0, (p - 0.38) / 0.62);
        text.y = startY - 14 * q;
        if (p < 0.38) text.alpha = 0;
        else if (p < 0.46) text.alpha = (p - 0.38) / 0.08;
        else if (p > 0.9) text.alpha = (1 - p) / 0.1;
        else text.alpha = 1;
        if (icon && !icon.destroyed) {
          const grey = Math.round(0xff - (0xff - 0x8a) * Math.min(1, q * 2));
          icon.tint = (grey << 16) | (grey << 8) | grey;
        }
      },
      onComplete: () => {
        text.visible = false;
      },
    });
  }

  hide(): void {
    this.tweens.cancelAll();
    this.visible = false;
    this.deltaHost.visible = false;
    this.noteText.visible = false;
    this.shieldBubble.visible = false;
    if (this.iconSprite) {
      this.iconSprite.destroy();
      this.iconSprite = null;
    }
  }

  destroyDisplay(): void {
    this.tweens.cancelAll();
    this.removeChildren();
  }

  private runProfile(
    profile: { startY: number; endY: number; startScale: number; peakScale: number; spin: number },
  ): void {
    const host = this.iconHost;
    host.y = profile.startY;
    host.scale.set(profile.startScale);
    host.angle = profile.spin;
    // Single chained sequence so the icon's lifetime matches the
    // 1800ms central-card slot exactly: 700ms enter → 920ms full
    // hold → 180ms fade. Producer feedback 2026-05-26 — previously
    // the hold timer ran in PARALLEL with the enter tween, so the
    // fade fired at t=920ms (700ms before the card hid).
    this.tweens.start({
      durationMs: 700,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        host.y = profile.startY + (profile.endY - profile.startY) * p;
        const s =
          profile.startScale + (profile.peakScale - profile.startScale) * p;
        host.scale.set(s);
        host.angle = profile.spin * (1 - p);
        host.alpha = p < 0.15 ? p / 0.15 : Math.min(1, p * 1.1);
      },
      onComplete: () => {
        // Hold full-opacity until just before the central card hides.
        this.tweens.start({
          durationMs: 920,
          easing: Easings.easeOutCubic,
          onUpdate: () => {},
          onComplete: () => {
            this.tweens.start({
              durationMs: 180,
              easing: Easings.easeOutCubic,
              onUpdate: (p) => {
                host.alpha = 1 - p;
                host.scale.set(profile.peakScale * (1 - p * 0.05));
              },
            });
          },
        });
      },
    });
  }
}

function animationProfile(cfg: SeatFxPlayConfig): {
  startY: number;
  endY: number;
  startScale: number;
  peakScale: number;
  spin: number;
} {
  // Default — pulse-style.
  let profile = {
    startY: 0,
    endY: 0,
    startScale: 0.6,
    peakScale: 1,
    spin: 0,
  };
  switch (cfg.itemId) {
    case 'BOMB':
    case 'COIN_STASH':
    case 'MAGNET':
      profile = {
        startY: -28,
        endY: 0,
        startScale: 0.7,
        peakScale: 1,
        spin: -15,
      };
      break;
    case 'LIGHTNING':
      profile = {
        startY: -36,
        endY: 0,
        startScale: 0.8,
        peakScale: 1,
        spin: 0,
      };
      break;
    case 'HIT':
    case 'POW':
      profile = {
        startY: 0,
        endY: 0,
        startScale: 0.4,
        peakScale: 1.15,
        spin: 0,
      };
      break;
    case 'SHAKEDOWN':
      profile = {
        startY: 0,
        endY: 0,
        startScale: 0.55,
        peakScale: 1.05,
        spin: 8,
      };
      break;
    case 'BONUS_WEDGE':
      profile = {
        startY: 0,
        endY: 0,
        startScale: 0.4,
        peakScale: 1.2,
        spin: 12,
      };
      break;
    default:
      // BOOST / TRIPLE_BOOST / X2_BONUS / SHIELD → default pulse
      break;
  }
  // Override: transfer source uses the "collect" beat (gain flavour).
  if (
    cfg.isSource &&
    cfg.seatDelta > 0 &&
    (cfg.itemId === 'LIGHTNING' || cfg.itemId === 'SHAKEDOWN')
  ) {
    profile.startY = -20;
    profile.endY = 0;
    profile.spin = 0;
  }
  return profile;
}
